import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlus, FiSearch, FiCalendar, FiPrinter, FiRefreshCw,
  FiFileText, FiChevronDown, FiChevronUp, FiFilter, FiX,
  FiDownload, FiBriefcase, FiMail, FiSend, FiEdit2, FiEye
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { documentAPI, companyAPI, settingsAPI, emailAPI, pdfLayoutAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  generateInvoicePDF,
  generatePackingListPDF,
  generateCertificateOfOriginPDF,
  generateBillOfExchangeFirstPDF,
  generateBillOfExchangeSecondPDF,
  generateInsuranceLetterPDF,
  generateNonNegotiableDocsPDF,
  generateFormSDFPDF,
  generateTruckOperatorsPDF,
  generateTruckReceiptPDF,
  generateTruckReceiptNCCPDF,
  generateTruckReceiptNCCCalibrationPDF,
  generateLCCompliancePDF,
  generateImporterNameAddressPDF,
  generatePreShipmentInspectionPDF,
  generateWeightMeasurementPDF,
  generateFaxTransmissionPDF,
  generateShipmentAdvicePDF,
  generateShipmentAdviceInsurancePDF,
  generateShipmentAdviceBuyerPDF,
  generateNonIsraeliFlagVesselPDF,
  generateBeneficiaryCertificatePDF,
  generateBeneficiaryCertificateTypeIIPDF,
  generatePhytosanitaryCertificatePDF,
  generateRadioactivityTestCertificatePDF,
  generateHealthCertificatePDF,
  generateLetterToBankPDF,
  generateTermsConditionsPDF,
  generateCountryOfOriginCertificatePDF,
} from '../PDF/PDFGenerator';
import TruckReceiptEditModal from './TruckReceiptEditModal';
import PdfDataEditModal from './PdfDataEditModal';
import TruckReceiptLayoutEditor from '../PDF/TruckReceiptLayoutEditor';
import { TRUCK_RECEIPT_NCC_DOC_TYPE } from '../PDF/TruckReceiptNCCPDF';
import { TRUCK_RECEIPT_NNC_DOC_TYPE } from '../PDF/OtherDocumentsPDF';
import { PDF_EDIT_SCHEMAS } from '../PDF/pdfEditSchemas';

// Preview types that print onto pre-printed stationery, mapped to the layout
// profile doc type their saved print alignment is stored under.
const LAYOUT_DOC_TYPES = {
  truckReceipt: TRUCK_RECEIPT_NNC_DOC_TYPE,
  truckReceiptNCC: TRUCK_RECEIPT_NCC_DOC_TYPE,
  truckReceiptNCCCalibration: TRUCK_RECEIPT_NCC_DOC_TYPE,
};

// ─── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (v) =>
  v != null && v !== ''
    ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// ─── PDF Menu Items ─────────────────────────────────────────────────────────
const PDF_MENU_ITEMS = [
  { type: 'invoice', icon: '📄', label: 'Invoice', previewTitle: '📄 Commercial Invoice' },
  { type: 'packing', icon: '📦', label: 'Packing List', previewTitle: '📦 Packing List' },
  { type: 'formSDF', icon: '📝', label: 'Form SDF', previewTitle: '📝 Form SDF' },
  { type: 'truckReceipt', icon: '🧾', label: 'Truck Receipt (NNC)', previewTitle: '🧾 Truck Receipt (NNC)' },
  { type: 'truckReceiptNCC', icon: '🧾', label: 'Truck Receipt (NCC)', previewTitle: '🧾 Truck Receipt (NCC)' },
  // Not a shipping document — a maintenance sheet for aligning the NCC overlay
  // to the pre-printed stationery. Page 1 goes on a blank form, page 2 is the key.
  { type: 'truckReceiptNCCCalibration', icon: '📐', label: 'Truck Receipt (NCC) — Calibration Sheet', previewTitle: '📐 Truck Receipt (NCC) Calibration Sheet' },
  { type: 'certificate', icon: '📜', label: 'Certificate of Origin', previewTitle: '📜 Certificate of Origin' },
  { type: 'billFirst', icon: '💵', label: 'First Bill of Exchange', previewTitle: '💵 First Bill of Exchange' },
  { type: 'billSecond', icon: '💵', label: 'Second Bill of Exchange', previewTitle: '💵 Second Bill of Exchange' },
  { type: 'insurance', icon: '🛡️', label: 'Letter to Insurance Co.', previewTitle: '🛡️ Letter to Insurance Company' },
  { type: 'nonNegotiable', icon: '📋', label: 'Non-Negotiable Documents', previewTitle: '📋 Non-Negotiable Documents' },
  { type: 'truckOperators', icon: '🚛', label: 'Registered Truck Operators', previewTitle: '🚛 Registered Truck Operators' },
  { type: 'countryOfOriginCert', icon: '🌍', label: 'Country of Origin Certificate', previewTitle: '🌍 Country of Origin Certificate' },
  { type: 'lcCompliance', icon: '✅', label: 'Terms & Conditions of L/C Complied With', previewTitle: '✅ Terms & Conditions of L/C Compliance' },
  { type: 'importerNameAddress', icon: '🏷️', label: 'Importer Name, Address and TIN No. Printed', previewTitle: '🏷️ Importer Name, Address & TIN' },
  { type: 'preShipmentInspection', icon: '🔍', label: 'Pre-Shipment Inspection by Beneficiary', previewTitle: '🔍 Pre-Shipment Inspection Certificate' },
  { type: 'weightMeasurement', icon: '⚖️', label: 'Weight and Measurement List', previewTitle: '⚖️ Weight & Measurement List' },
  { type: 'faxTransmission', icon: '📠', label: 'Fax Transmission', previewTitle: '📠 Fax Transmission' },
  { type: 'shipmentAdvice', icon: '📨', label: 'Shipment Advice to the Opening Bank', previewTitle: '📨 Shipment Advice to the Opening Bank' },
  { type: 'shipmentAdviceInsurance', icon: '🛡️', label: 'Shipment Advice to the Insurance Co.', previewTitle: '🛡️ Shipment Advice to Insurance Co.' },
  { type: 'shipmentAdviceBuyer', icon: '📨', label: 'Shipment Advice to the Buyer', previewTitle: '📨 Shipment Advice to the Buyer' },
  { type: 'nonIsraeliFlagVessel', icon: '🚢', label: 'Non-Israeli Flag Vessel Cert. from the Carrier', previewTitle: '🚢 Non-Israeli Flag Vessel Certificate' },
  { type: 'beneficiaryCertificate', icon: '📋', label: 'Beneficiary Certificate', previewTitle: '📋 Beneficiary Certificate' },
  { type: 'beneficiaryCertificateTypeII', icon: '📑', label: 'Beneficiary Certificate Content Type II', previewTitle: '📑 Beneficiary Certificate Content Type II' },
  { type: 'phytosanitaryCertificate', icon: '🌿', label: 'Phytosanitary Certificate by ABC International', previewTitle: '🌿 Phytosanitary Certificate' },
  { type: 'radioactivityTestCertificate', icon: '☢️', label: 'Radioactivity Test Certificate by ABC International', previewTitle: '☢️ Radioactivity Test Certificate' },
  { type: 'healthCertificate', icon: '🏥', label: 'Health Certificate by ABC International', previewTitle: '🏥 Health Certificate' },
  { type: 'letterToBank', icon: '🏦', label: 'Letter to Bank for Negotiation (Front)', previewTitle: '🏦 Letter to Bank for Negotiation (Front)' },
  { type: 'termsConditions', icon: '🏦', label: 'Letter to Bank for Negotiation (Back)', previewTitle: '🏦 Letter to Bank for Negotiation (Back)' },
];

const firstDayOfFY = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-04-01`;
};

// ─── Responsive hook ────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    let raf;
    const handle = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setWidth(window.innerWidth));
    };
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('resize', handle);
      cancelAnimationFrame(raf);
    };
  }, []);
  return width;
}

// ─── Responsive CSS (injected once) ─────────────────────────────────────────────
const RESPONSIVE_CSS_ID = 'doclist-responsive-css';
function injectResponsiveCSS() {
  if (document.getElementById(RESPONSIVE_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = RESPONSIVE_CSS_ID;
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    .dl-table-scroll::-webkit-scrollbar { height: 6px; }
    .dl-table-scroll::-webkit-scrollbar-track { background: #f0fdf4; }
    .dl-table-scroll::-webkit-scrollbar-thumb { background: #a7f3d0; border-radius: 3px; }
    .dl-table-scroll::-webkit-scrollbar-thumb:hover { background: #6ee7b7; }
  `;
  document.head.appendChild(style);
}

// ─── Styles ─────────────────────────────────────────────────────────────────────
const getStyles = (w) => {
  const isMobile = w < 768;
  const isTablet = w >= 768 && w < 1024;

  return {
    page: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: isMobile ? '0 2px' : 0,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: isMobile ? 16 : 24,
      flexWrap: 'wrap',
      gap: isMobile ? 12 : 16,
    },
    titleWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? 10 : 14,
    },
    titleIcon: {
      width: isMobile ? 38 : 48,
      height: isMobile ? 38 : 48,
      borderRadius: isMobile ? 10 : 14,
      background: 'linear-gradient(135deg, #059669, #10b981)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
      flexShrink: 0,
    },
    title: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: isMobile ? 18 : isTablet ? 22 : 26,
      fontWeight: 700,
      color: '#064e3b',
      margin: 0,
    },
    subtitle: {
      fontSize: isMobile ? 11 : 13,
      color: '#6b7280',
      marginTop: 2,
    },
    btnPrimary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: isMobile ? 6 : 8,
      padding: isMobile ? '9px 14px' : '11px 22px',
      background: 'linear-gradient(135deg, #059669, #10b981)',
      color: '#fff',
      border: 'none',
      borderRadius: isMobile ? 10 : 12,
      fontSize: isMobile ? 13 : 14,
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
      fontFamily: "'Inter', sans-serif",
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    },
    btnSecondary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: isMobile ? 4 : 8,
      padding: isMobile ? '9px 12px' : '10px 18px',
      background: '#fff',
      color: '#047857',
      border: '2px solid rgba(16, 185, 129, 0.25)',
      borderRadius: isMobile ? 10 : 12,
      fontSize: isMobile ? 13 : 14,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: "'Inter', sans-serif",
      whiteSpace: 'nowrap',
    },
    filterCard: {
      background: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(16, 185, 129, 0.12)',
      borderRadius: isMobile ? 12 : 16,
      padding: isMobile ? '14px 12px' : '20px 24px',
      marginBottom: isMobile ? 12 : 20,
      boxShadow: '0 4px 12px rgba(6, 78, 59, 0.08)',
    },
    filterRow: {
      display: 'flex',
      alignItems: isMobile ? 'stretch' : 'flex-end',
      gap: isMobile ? 10 : 16,
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    filterLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: '#047857',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    filterInput: {
      padding: isMobile ? '10px 14px' : '9px 14px',
      border: '2px solid rgba(16, 185, 129, 0.2)',
      borderRadius: 8,
      fontSize: 14,
      outline: 'none',
      fontFamily: "'Inter', sans-serif",
      background: '#f0fdf4',
      color: '#064e3b',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      minWidth: isMobile ? 0 : 160,
      width: isMobile ? '100%' : undefined,
      boxSizing: 'border-box',
    },
    tableContainer: {
      background: '#fff',
      borderRadius: isMobile ? 12 : 16,
      boxShadow: '0 4px 20px rgba(6, 78, 59, 0.1)',
      overflow: 'hidden',
      border: '1px solid rgba(16, 185, 129, 0.08)',
    },
    tableScroll: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? undefined : isTablet ? 1000 : 1280,
    },
    thead: {
      background: 'linear-gradient(135deg, #064e3b, #047857)',
    },
    th: {
      padding: isTablet ? '12px 10px' : '16px 14px',
      color: '#fff',
      fontWeight: 700,
      fontSize: isTablet ? 11 : 13,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      textAlign: 'left',
      whiteSpace: 'nowrap',
      borderBottom: 'none',
      borderRight: '1px solid rgba(255,255,255,0.1)',
    },
    td: {
      padding: isTablet ? '10px 10px' : '14px 14px',
      borderBottom: '1px solid rgba(16, 185, 129, 0.08)',
      fontSize: isTablet ? 12 : 13,
      color: '#1e293b',
      whiteSpace: 'nowrap',
      borderRight: '1px solid rgba(16, 185, 129, 0.04)',
    },
    tdRight: {
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
    tr: {
      cursor: 'pointer',
      transition: 'background 0.15s ease',
    },
    footerRow: {
      background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
      borderTop: '2px solid #059669',
    },
    footerLabel: {
      padding: '14px 14px',
      fontWeight: 700,
      fontSize: 13,
      color: '#064e3b',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    footerValue: {
      padding: '14px 14px',
      fontWeight: 700,
      fontSize: 14,
      color: '#059669',
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
    emptyState: {
      textAlign: 'center',
      padding: isMobile ? '40px 16px' : '60px 20px',
    },
    emptyIcon: {
      width: isMobile ? 56 : 72,
      height: isMobile ? 56 : 72,
      borderRadius: '50%',
      background: '#ecfdf5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
    },
    loadingOverlay: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '40px 16px' : '60px 20px',
      gap: 16,
    },
    spinner: {
      width: 40,
      height: 40,
      border: '3px solid #d1fae5',
      borderTopColor: '#059669',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
    rowNumber: {
      color: '#94a3b8',
      fontWeight: 500,
      fontSize: 12,
      textAlign: 'center',
      width: 36,
    },
    badge: {
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 9999,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
    },
    // ─── Mobile Card Styles ─────────────────────────────────────────
    card: {
      background: '#fff',
      borderRadius: 14,
      border: '1px solid rgba(16,185,129,0.1)',
      padding: '14px 16px',
      marginBottom: 10,
      boxShadow: '0 2px 8px rgba(6,78,59,0.06)',
      cursor: 'pointer',
      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 10,
      gap: 8,
    },
    cardInvoice: {
      fontWeight: 700,
      fontSize: 15,
      color: '#047857',
    },
    cardBuyer: {
      fontSize: 13,
      color: '#374151',
      fontWeight: 500,
      marginTop: 2,
    },
    cardGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '6px 16px',
      fontSize: 12,
      color: '#6b7280',
    },
    cardLabel: {
      fontSize: 10,
      fontWeight: 600,
      color: '#9ca3af',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    cardValue: {
      fontSize: 13,
      color: '#1e293b',
      fontWeight: 500,
    },
    cardFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 10,
      paddingTop: 10,
      borderTop: '1px solid rgba(16,185,129,0.08)',
    },
    cardAmount: {
      fontWeight: 700,
      fontSize: 16,
      color: '#064e3b',
    },
    cardActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    // ─── Mobile Totals Bar ──────────────────────────────────────────
    mobileTotals: {
      background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
      borderRadius: 12,
      padding: '12px 16px',
      marginTop: 4,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: '1px solid rgba(16,185,129,0.15)',
    },
  };
};

// ─── Column Definitions ─────────────────────────────────────────────────────────
const columns = [
  { key: '#',              label: '##',          align: 'center', width: 50 },
  { key: 'invoice_no',     label: 'Invoice No',  align: 'left', width: 140 },
  { key: 'document_type',  label: 'Type',        align: 'left', width: 130 },
  { key: 'proforma_no',   label: 'Proforma Ref', align: 'left', width: 140 },
  { key: 'lc_no',          label: 'L/C No & Dt', align: 'left', width: 150 },
  { key: 'buyer_name',     label: 'Buyer',       align: 'left', width: 180 },
  { key: 'commodity',      label: 'Commodity',   align: 'left', width: 140 },
  { key: 'sb_no',          label: 'S.B. No',     align: 'left', width: 120 },
  { key: 'export_date',    label: 'Export Dt',   align: 'center', width: 110 },
  { key: 'bill_ref',       label: 'Bill Ref',    align: 'left', width: 120 },
  { key: 'total_usd',      label: 'INV.($)',     align: 'right', width: 120 },
  { key: 'due_date',       label: 'Due Dt',      align: 'center', width: 110 },
  { key: 'payment_date',   label: 'Pymt Dt',     align: 'center', width: 130 },
  { key: 'fob_inr',        label: 'FOB(INR)',    align: 'right', width: 130 },
  { key: 'drawback',       label: 'Drawback',    align: 'right', width: 130 },
  { key: 'download',       label: '',            align: 'center', width: 70 },
];

// ─── Component ──────────────────────────────────────────────────────────────────
export default function CommercialList() {
  const navigate = useNavigate();
  const { activeCompany } = useAuth();
  const printRef = useRef();
  const winWidth = useWindowWidth();
  const isMobile = winWidth < 768;
  const styles = React.useMemo(() => getStyles(winWidth), [winWidth]);

  // Inject responsive CSS once
  useEffect(() => { injectResponsiveCSS(); }, []);

  // State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(firstDayOfFY());
  const [toDate, setToDate] = useState(todayISO());
  const [fyLoaded, setFyLoaded] = useState(false);
  const [sortField, setSortField] = useState('export_date');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [downloadMenuId, setDownloadMenuId] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [pdfPreview, setPdfPreview] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [truckReceiptEdit, setTruckReceiptEdit] = useState(null); // { docId, docType }
  const [pdfEdit, setPdfEdit] = useState(null); // { docId, docType }
  const [layoutEditor, setLayoutEditor] = useState(null); // { doc, company, authority }

  // ── Load active financial year dates ────────────────────────────────────────
  useEffect(() => {
    settingsAPI.getFinancialYears().then((res) => {
      const years = res.data || [];
      const active = years.find((fy) => fy.is_active);
      if (active) {
        setFromDate(active.start_date);
        setToDate(active.end_date);
      }
      setFyLoaded(true);
    }).catch(() => {
      setFyLoaded(true);
    });
  }, []);

  // ── Fetch all companies ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await companyAPI.getAll();
        setCompanies(res.data || []);
      } catch (err) {
        console.error('Failed to load companies:', err);
        setCompanies([]);
      }
    };
    fetchCompanies();
  }, []);

  // Download modal closes via overlay click or item selection

  // ── Download handler ────────────────────────────────────────────────────────
  const handleDownload = async (docId, type) => {
    setDownloadMenuId(null);
    setDownloading(docId);
    try {
      const docRes = await documentAPI.getById(docId);
      const fullDoc = docRes.data;

      const compRes = await companyAPI.getById(fullDoc.company_id);
      const company = compRes.data;

      // Find primary active authority
      const authority = (company.authorities || []).find(
        (a) => a.is_primary && a.status === 'Active'
      ) || (company.authorities || [])[0] || null;

      if (type === 'invoice') {
        generateInvoicePDF(fullDoc, company, authority);
        toast.success('Invoice PDF downloaded');
      } else {
        generatePackingListPDF(fullDoc, company, authority);
        toast.success('Packing List PDF downloaded');
      }
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to generate PDF');
    } finally {
      setDownloading(null);
    }
  };

  // ── Active saved print-layout profile for a pre-printed form overlay ─────
  // Null (no profile / fetch failed) falls back to the built-in geometry.
  const fetchLayoutProfile = async (docType) => {
    try {
      const res = await pdfLayoutAPI.getAll(docType);
      const active = (res.data || []).find((p) => p.is_active);
      return active ? JSON.parse(active.data || '{}') : null;
    } catch (err) {
      console.error('Failed to load print layout profile:', err);
      return null;
    }
  };

  // ── PDF Preview handler ──────────────────────────────────────────────────
  const handlePdfPreview = async (docId, type) => {
    setDownloading(docId);
    try {
      const docRes = await documentAPI.getById(docId);
      const fullDoc = docRes.data;

      const compRes = await companyAPI.getById(fullDoc.company_id);
      const company = compRes.data;

      // Find primary active authority
      const authority = (company.authorities || []).find(
        (a) => a.is_primary && a.status === 'Active'
      ) || (company.authorities || [])[0] || null;

      let pdfBlob;
      if (type === 'invoice') {
        pdfBlob = generateInvoicePDF(fullDoc, company, authority, true);
      } else if (type === 'packing') {
        pdfBlob = generatePackingListPDF(fullDoc, company, authority, true);
      } else if (type === 'certificate') {
        pdfBlob = generateCertificateOfOriginPDF(fullDoc, company, authority, true);
      } else if (type === 'billFirst') {
        pdfBlob = generateBillOfExchangeFirstPDF(fullDoc, company, authority, true);
      } else if (type === 'billSecond') {
        pdfBlob = generateBillOfExchangeSecondPDF(fullDoc, company, authority, true);
      } else if (type === 'insurance') {
        pdfBlob = generateInsuranceLetterPDF(fullDoc, company, authority, true);
      } else if (type === 'nonNegotiable') {
        pdfBlob = generateNonNegotiableDocsPDF(fullDoc, company, authority, true);
      } else if (type === 'formSDF') {
        pdfBlob = generateFormSDFPDF(fullDoc, company, authority, true);
      } else if (type === 'truckReceipt') {
        pdfBlob = generateTruckReceiptPDF(fullDoc, company, authority, true, await fetchLayoutProfile(LAYOUT_DOC_TYPES.truckReceipt));
      } else if (type === 'truckReceiptNCC') {
        pdfBlob = generateTruckReceiptNCCPDF(fullDoc, company, authority, true, await fetchLayoutProfile(LAYOUT_DOC_TYPES.truckReceiptNCC));
      } else if (type === 'truckReceiptNCCCalibration') {
        pdfBlob = generateTruckReceiptNCCCalibrationPDF(fullDoc, company, authority, true, await fetchLayoutProfile(LAYOUT_DOC_TYPES.truckReceiptNCC));
      } else if (type === 'truckOperators') {
        pdfBlob = generateTruckOperatorsPDF(fullDoc, company, authority, true);
      } else if (type === 'countryOfOriginCert') {
        pdfBlob = generateCountryOfOriginCertificatePDF(fullDoc, company, authority, true);
      } else if (type === 'lcCompliance') {
        pdfBlob = generateLCCompliancePDF(fullDoc, company, authority, true);
      } else if (type === 'importerNameAddress') {
        pdfBlob = generateImporterNameAddressPDF(fullDoc, company, authority, true);
      } else if (type === 'preShipmentInspection') {
        pdfBlob = generatePreShipmentInspectionPDF(fullDoc, company, authority, true);
      } else if (type === 'weightMeasurement') {
        pdfBlob = generateWeightMeasurementPDF(fullDoc, company, authority, true);
      } else if (type === 'faxTransmission') {
        pdfBlob = generateFaxTransmissionPDF(fullDoc, company, authority, true);
      } else if (type === 'shipmentAdvice') {
        pdfBlob = generateShipmentAdvicePDF(fullDoc, company, authority, true);
      } else if (type === 'shipmentAdviceInsurance') {
        pdfBlob = generateShipmentAdviceInsurancePDF(fullDoc, company, authority, true);
      } else if (type === 'shipmentAdviceBuyer') {
        pdfBlob = generateShipmentAdviceBuyerPDF(fullDoc, company, authority, true);
      } else if (type === 'nonIsraeliFlagVessel') {
        pdfBlob = generateNonIsraeliFlagVesselPDF(fullDoc, company, authority, true);
      } else if (type === 'beneficiaryCertificate') {
        pdfBlob = generateBeneficiaryCertificatePDF(fullDoc, company, authority, true);
      } else if (type === 'beneficiaryCertificateTypeII') {
        pdfBlob = generateBeneficiaryCertificateTypeIIPDF(fullDoc, company, authority, true);
      } else if (type === 'phytosanitaryCertificate') {
        pdfBlob = generatePhytosanitaryCertificatePDF(fullDoc, company, authority, true);
      } else if (type === 'radioactivityTestCertificate') {
        pdfBlob = generateRadioactivityTestCertificatePDF(fullDoc, company, authority, true);
      } else if (type === 'healthCertificate') {
        pdfBlob = generateHealthCertificatePDF(fullDoc, company, authority, true);
      } else if (type === 'letterToBank') {
        pdfBlob = generateLetterToBankPDF(fullDoc, company, authority, true);
      } else if (type === 'termsConditions') {
        pdfBlob = generateTermsConditionsPDF(fullDoc, company, authority, true);
      }

      if (pdfBlob) {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setPdfPreview({
          doc: fullDoc,
          type,
          url: pdfUrl,
          company,
          authority,
        });
      }
    } catch (err) {
      console.error('PDF preview error:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to preview PDF');
    } finally {
      setDownloading(null);
    }
  };

  // ── Close PDF Preview ────────────────────────────────────────────────────
  const closePdfPreview = () => {
    if (pdfPreview?.url) {
      URL.revokeObjectURL(pdfPreview.url);
    }
    setPdfPreview(null);
  };

  // ── Email send handler ──────────────────────────────────────────────────────
  const handleSendEmail = async () => {
    if (!emailModal) return;
    const { to, subject, body } = emailModal;
    if (!to) {
      toast.error('Please enter a recipient email address');
      return;
    }
    setSendingEmail(true);
    try {
      // Fetch the blob from the URL
      const response = await fetch(pdfPreview.url);
      const blob = await response.blob();

      const pdfTypeLabel = PDF_MENU_ITEMS.find(i => i.type === pdfPreview.type)?.label || 'Document';
      const filename = `${pdfTypeLabel.replace(/\s+/g, '_')}_${(pdfPreview.doc.invoice_no || 'Document').replace(/\s+/g, '_')}.pdf`;

      const formData = new FormData();
      formData.append('to', to);
      formData.append('subject', subject);
      formData.append('body', body);
      formData.append('filename', filename);
      formData.append('pdf', blob, filename);

      await emailAPI.sendDocument(formData);
      toast.success('Email sent successfully!');
      setEmailModal(null);
    } catch (err) {
      console.error('Email send error:', err);
      toast.error(err.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchDocuments = useCallback(async () => {
    if (!fyLoaded) return;
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (selectedCompanyId) params.company_id = selectedCompanyId;
      const res = await documentAPI.getAll(params);
      setDocuments(Array.isArray(res.data) ? res.data : res.data.documents || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, fyLoaded, selectedCompanyId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // ── Sort ────────────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (field === '#' || field === 'download') return;
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // ── Filter + Sort pipeline ──────────────────────────────────────────────────
  const processedDocs = React.useMemo(() => {
    let list = [...documents];

    // Filter out Proforma invoices - show only Commercial Invoices
    list = list.filter(d => d.document_type !== 'Proforma Invoice');

    // Text search
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (d) =>
          (d.invoice_no || '').toLowerCase().includes(q) ||
          (d.buyer_name || '').toLowerCase().includes(q) ||
          (d.commodity || '').toLowerCase().includes(q) ||
          (d.lc_no || '').toLowerCase().includes(q) ||
          (d.sb_no || '').toLowerCase().includes(q) ||
          (d.bill_ref || '').toLowerCase().includes(q)
      );
    }

    // Sort
    list.sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (va == null) va = '';
      if (vb == null) vb = '';

      // Numeric columns
      if (['total_usd', 'fob_inr', 'drawback'].includes(sortField)) {
        va = parseFloat(va) || 0;
        vb = parseFloat(vb) || 0;
      }
      // Date columns
      if (['export_date', 'due_date', 'payment_date', 'lc_opening_date'].includes(sortField)) {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      }

      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [documents, searchTerm, sortField, sortDir]);

  // ── Totals ──────────────────────────────────────────────────────────────────
  const totals = React.useMemo(() => {
    let totalUsd = 0;
    let totalFob = 0;
    let totalDrawback = 0;
    processedDocs.forEach((d) => {
      totalUsd += parseFloat(d.total_usd) || 0;
      totalFob += parseFloat(d.fob_inr) || 0;
      totalDrawback += parseFloat(d.drawback) || 0;
    });
    return { totalUsd, totalFob, totalDrawback };
  }, [processedDocs]);

  // ── Visible Columns (hide columns with no data) ────────────────────────────
  const visibleColumns = React.useMemo(() => {
    // Always show these essential columns
    const alwaysShow = ['#', 'invoice_no', 'document_type', 'buyer_name', 'total_usd', 'download'];

    return columns.filter((col) => {
      // Always show essential columns
      if (alwaysShow.includes(col.key)) return true;

      // Check if any document has data for this column
      const hasData = processedDocs.some((doc) => {
        if (col.key === 'proforma_no') return doc.proforma != null;
        const value = doc[col.key];
        return value != null && value !== '' && value !== '-';
      });

      return hasData;
    });
  }, [processedDocs]);

  // Width the table actually needs. A fixed min-width made the table overflow its
  // container when only a few columns were visible, pushing the last data column
  // (INV.($)) underneath the sticky action column.
  const tableMinWidth = React.useMemo(
    () => visibleColumns.reduce((sum, col) => sum + (col.width || 120), 0),
    [visibleColumns]
  );

  // ── Print ───────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the report');
      return;
    }

    const companyName = activeCompany?.exporter_name || 'Export Management System';

    const rows = processedDocs
      .map(
        (doc, i) => `
      <tr>
        <td style="text-align:center;padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${i + 1}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${doc.invoice_no || ''}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:11px;font-weight:600;">${doc.document_type === 'Proforma Invoice' ? 'Proforma' : doc.document_type === 'COMMERCIAL INVOICE' ? 'Commercial' : doc.document_type || 'Invoice'}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${doc.lc_no || ''}${doc.lc_opening_date ? ' / ' + fmtDate(doc.lc_opening_date) : ''}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${doc.buyer_name || ''}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${doc.commodity || ''}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${doc.sb_no || ''}</td>
        <td style="text-align:center;padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${fmtDate(doc.export_date)}</td>
        <td style="padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${doc.bill_ref || ''}</td>
        <td style="text-align:right;padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${fmt(doc.total_usd)}</td>
        <td style="text-align:center;padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${fmtDate(doc.due_date)}</td>
        <td style="text-align:center;padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${fmtDate(doc.payment_date)}</td>
        <td style="text-align:right;padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${fmt(doc.fob_inr)}</td>
        <td style="text-align:right;padding:6px 8px;border:1px solid #d1d5db;font-size:12px;">${fmt(doc.drawback)}</td>
      </tr>`
      )
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Document Report - ${companyName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #111; }
          h1 { font-size: 18px; color: #064e3b; margin-bottom: 4px; }
          .sub { font-size: 13px; color: #6b7280; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #064e3b; color: #fff; padding: 8px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border: 1px solid #064e3b; }
          tfoot td { background: #ecfdf5; font-weight: 700; font-size: 13px; color: #064e3b; padding: 8px; border: 1px solid #d1d5db; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${companyName}</h1>
        <div class="sub">
          Shipment Document Report &mdash;
          ${fromDate ? fmtDate(fromDate) : 'All'} to ${toDate ? fmtDate(toDate) : 'All'}
          &bull; ${processedDocs.length} record(s)
        </div>
        <table>
          <thead>
            <tr>
              <th style="text-align:center">##</th>
              <th>Invoice No</th>
              <th>Type</th>
              <th>L/C No & Dt</th>
              <th>Buyer</th>
              <th>Commodity</th>
              <th>S.B. No</th>
              <th style="text-align:center">Export Dt</th>
              <th>Bill Ref</th>
              <th style="text-align:right">INV.($)</th>
              <th style="text-align:center">Due Dt</th>
              <th style="text-align:center">Pymt Dt</th>
              <th style="text-align:right">FOB(INR)</th>
              <th style="text-align:right">Drawback</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr>
              <td colspan="12" style="text-align:right;padding:8px;border:1px solid #d1d5db;">
                TOTAL
              </td>
              <td style="text-align:right;padding:8px;border:1px solid #d1d5db;">
                ${fmt(totals.totalFob)}
              </td>
              <td style="text-align:right;padding:8px;border:1px solid #d1d5db;">
                ${fmt(totals.totalDrawback)}
              </td>
            </tr>
          </tfoot>
        </table>
        <script>window.onload=function(){window.print();}</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // ── Download button (shared between table and card) ─────────────────────────
  const renderDownloadBtn = (doc) => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        setDownloadMenuId(doc.id);
      }}
      disabled={downloading === doc.id}
      style={{
        background: 'none',
        border: '1.5px solid rgba(16,185,129,0.3)',
        borderRadius: 8,
        padding: '5px 8px',
        cursor: downloading === doc.id ? 'wait' : 'pointer',
        color: '#047857',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        fontWeight: 500,
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#ecfdf5';
        e.currentTarget.style.borderColor = '#10b981';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'none';
        e.currentTarget.style.borderColor = 'rgba(16,185,129,0.3)';
      }}
    >
      {downloading === doc.id ? (
        <div style={{
          width: 14, height: 14,
          border: '2px solid #d1fae5',
          borderTopColor: '#059669',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      ) : (
        <FiEye size={14} />
      )}
    </button>
  );

  // ── Download Modal ─────────────────────────────────────────────────────────
  const renderDownloadModal = () => {
    if (!downloadMenuId) return null;
    const doc = documents.find(d => d.id === downloadMenuId);
    if (!doc) return null;
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setDownloadMenuId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 20px 60px rgba(6,78,59,0.25)',
              width: '100%',
              maxWidth: 480,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 24px',
              borderBottom: '1px solid rgba(16,185,129,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#064e3b', fontFamily: "'Poppins', sans-serif" }}>
                  Download Documents
                </div>
                <div style={{ fontSize: 12, color: '#047857', marginTop: 2 }}>
                  {doc.invoice_no || 'Invoice'} — {doc.buyer_name || 'Unknown Buyer'}
                </div>
              </div>
              <button
                onClick={() => setDownloadMenuId(null)}
                style={{
                  background: 'rgba(6,78,59,0.08)',
                  border: 'none',
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#064e3b',
                  fontSize: 18,
                }}
              >
                ✕
              </button>
            </div>

            {/* Items Grid */}
            <div style={{
              padding: 16,
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 8,
            }}>
              {PDF_MENU_ITEMS.map((item) => {
                // Every document gets an edit pencil: both Truck Receipt
                // variants (NNC/NCC) open the dedicated form on their shared
                // print data, invoice/packing open the invoice editor,
                // everything else opens the generic print-data editor.
                const isTruckReceipt =
                  item.type === 'truckReceipt' || item.type === 'truckReceiptNCC';
                const isInvoiceLike = item.type === 'invoice' || item.type === 'packing';
                const hasSchema = !!PDF_EDIT_SCHEMAS[item.type];
                const canEdit = isTruckReceipt || isInvoiceLike || hasSchema;

                const handleEdit = (e) => {
                  e.stopPropagation();
                  const id = downloadMenuId;
                  setDownloadMenuId(null);
                  if (isTruckReceipt) {
                    setTruckReceiptEdit({ docId: id, docType: item.type });
                  } else if (isInvoiceLike) {
                    navigate(`/commercial-invoices/edit/${id}`);
                  } else {
                    setPdfEdit({ docId: id, docType: item.type });
                  }
                };

                const tile = (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handlePdfPreview(downloadMenuId, item.type)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '12px 14px',
                      paddingRight: canEdit ? 34 : 14,
                      width: '100%',
                      height: '100%',
                      background: '#f8fdfb',
                      border: '1px solid rgba(16,185,129,0.15)',
                      borderRadius: 10,
                      textAlign: 'left',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#047857',
                      cursor: 'pointer',
                      fontFamily: "'Inter', sans-serif",
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5, #d1fae5)';
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.color = '#065f46';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8fdfb';
                      e.currentTarget.style.borderColor = 'rgba(16,185,129,0.15)';
                      e.currentTarget.style.color = '#047857';
                    }}
                  >
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
                    <span style={{ lineHeight: 1.3 }}>{item.label}</span>
                  </motion.button>
                );

                if (!canEdit) return <React.Fragment key={item.type}>{tile}</React.Fragment>;

                return (
                  <div key={item.type} style={{ position: 'relative' }}>
                    {tile}
                    <button
                      title={`Edit ${item.label} data`}
                      onClick={handleEdit}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#fff',
                        border: '1px solid rgba(16,185,129,0.3)',
                        borderRadius: 6,
                        color: '#047857',
                        cursor: 'pointer',
                      }}
                    >
                      <FiEdit2 size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // ── Render Cell Content (dynamic based on column key) ──────────────────────
  const renderCell = (doc, col, index) => {
    const commonStyle = { ...styles.td, textAlign: col.align || 'left' };

    switch (col.key) {
      case '#':
        return <td key={col.key} style={{ ...commonStyle, ...styles.rowNumber }}>{index + 1}</td>;

      case 'invoice_no':
        return (
          <td key={col.key} style={commonStyle}>
            <span style={{ fontWeight: 600, color: '#047857' }}>
              {doc.invoice_no || '-'}
            </span>
          </td>
        );

      case 'document_type':
        return (
          <td key={col.key} style={commonStyle}>
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              background: doc.document_type === 'Proforma Invoice' ? '#fef3c7' : '#dbeafe',
              color: doc.document_type === 'Proforma Invoice' ? '#92400e' : '#1e40af',
            }}>
              {doc.document_type === 'Proforma Invoice' ? 'Proforma' :
               doc.document_type === 'COMMERCIAL INVOICE' ? 'Commercial' :
               doc.document_type || 'Invoice'}
            </span>
          </td>
        );

      case 'proforma_no':
        return (
          <td key={col.key} style={commonStyle}>
            {doc.proforma ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  background: '#fef3c7',
                  color: '#92400e',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/proforma-invoices/${doc.proforma.id}`);
                }}
                title={`View Proforma: ${doc.proforma.invoice_no}`}
              >
                {doc.proforma.invoice_no}
              </span>
            ) : '-'}
          </td>
        );

      case 'lc_no':
        return (
          <td key={col.key} style={commonStyle}>
            {doc.lc_no || '-'}
            {doc.lc_opening_date && (
              <span style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                {fmtDate(doc.lc_opening_date)}
              </span>
            )}
          </td>
        );

      case 'buyer_name':
        return (
          <td key={col.key} style={{ ...commonStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {doc.buyer_name || '-'}
          </td>
        );

      case 'commodity':
        return (
          <td key={col.key} style={{ ...commonStyle, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {doc.commodity || '-'}
          </td>
        );

      case 'sb_no':
        return <td key={col.key} style={commonStyle}>{doc.sb_no || '-'}</td>;

      case 'export_date':
        return <td key={col.key} style={commonStyle}>{fmtDate(doc.export_date)}</td>;

      case 'bill_ref':
        return <td key={col.key} style={commonStyle}>{doc.bill_ref || '-'}</td>;

      case 'total_usd':
        return (
          <td key={col.key} style={{ ...commonStyle, fontWeight: 600 }}>
            {fmt(doc.total_usd)}
          </td>
        );

      case 'due_date':
        return <td key={col.key} style={commonStyle}>{fmtDate(doc.due_date)}</td>;

      case 'payment_date':
        return (
          <td key={col.key} style={commonStyle}>
            {doc.payment_date ? (
              <span style={{ ...styles.badge, background: '#d1fae5', color: '#065f46' }}>
                {fmtDate(doc.payment_date)}
              </span>
            ) : (
              <span style={{ ...styles.badge, background: '#fef3c7', color: '#92400e' }}>
                Pending
              </span>
            )}
          </td>
        );

      case 'fob_inr':
        return (
          <td key={col.key} style={{ ...commonStyle, fontWeight: 600 }}>
            {fmt(doc.fob_inr)}
          </td>
        );

      case 'drawback':
        return <td key={col.key} style={commonStyle}>{fmt(doc.drawback)}</td>;

      case 'download':
        return (
          <td key={col.key} style={{
            ...commonStyle,
            position: 'sticky',
            right: 0,
            background: '#fff',
            boxShadow: '-2px 0 8px rgba(0,0,0,0.06)',
            textAlign: 'center',
            padding: '12px 18px',
          }}>
            {renderDownloadBtn(doc)}
          </td>
        );

      default:
        return <td key={col.key} style={commonStyle}>-</td>;
    }
  };

  // ── Mobile card view ────────────────────────────────────────────────────────
  const renderMobileCards = () => {
    if (loading) {
      return (
        <div style={styles.loadingOverlay}>
          <div style={styles.spinner} />
          <span style={{ color: '#6b7280', fontSize: 14 }}>Loading documents...</span>
        </div>
      );
    }

    if (processedDocs.length === 0) {
      return (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <FiFileText size={28} color="#059669" />
          </div>
          <h3 style={{ color: '#064e3b', marginBottom: 8, fontFamily: "'Poppins', sans-serif", fontSize: 16 }}>
            No Documents Found
          </h3>
          <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
            {searchTerm
              ? 'No documents match your search criteria.'
              : 'Start by creating your first shipment document.'}
          </p>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/commercial-invoices/new')}
            style={styles.btnPrimary}
          >
            <FiPlus size={16} /> Create Document
          </motion.button>
        </div>
      );
    }

    return (
      <>
        <AnimatePresence>
          {processedDocs.map((doc, index) => (
            <motion.div
              key={doc.id || index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, delay: index * 0.03 }}
              style={styles.card}
              onClick={() => navigate(`/commercial-invoices/edit/${doc.id}`)}
            >
              {/* Card header */}
              <div style={styles.cardHeader}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={styles.cardInvoice}>{doc.invoice_no || '-'}</div>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 9,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      background: doc.document_type === 'Proforma Invoice' ? '#fef3c7' : '#dbeafe',
                      color: doc.document_type === 'Proforma Invoice' ? '#92400e' : '#1e40af',
                    }}>
                      {doc.document_type === 'Proforma Invoice' ? 'Proforma' :
                       doc.document_type === 'COMMERCIAL INVOICE' ? 'Commercial' :
                       doc.document_type || 'Invoice'}
                    </span>
                  </div>
                  <div style={styles.cardBuyer}>{doc.buyer_name || '-'}</div>
                </div>
                <div style={styles.cardActions}>
                  {doc.payment_date ? (
                    <span style={{
                      ...styles.badge,
                      background: '#d1fae5',
                      color: '#065f46',
                    }}>
                      Paid
                    </span>
                  ) : (
                    <span style={{
                      ...styles.badge,
                      background: '#fef3c7',
                      color: '#92400e',
                    }}>
                      Pending
                    </span>
                  )}
                  {renderDownloadBtn(doc)}
                </div>
              </div>

              {/* Card details grid */}
              <div style={styles.cardGrid}>
                {doc.proforma && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={styles.cardLabel}>Proforma Ref</div>
                    <div
                      style={{ ...styles.cardValue, color: '#92400e', fontWeight: 600, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/proforma-invoices/${doc.proforma.id}`);
                      }}
                    >
                      {doc.proforma.invoice_no}
                    </div>
                  </div>
                )}
                <div>
                  <div style={styles.cardLabel}>Commodity</div>
                  <div style={styles.cardValue}>{doc.commodity || '-'}</div>
                </div>
                <div>
                  <div style={styles.cardLabel}>Export Date</div>
                  <div style={styles.cardValue}>{fmtDate(doc.export_date) || '-'}</div>
                </div>
                <div>
                  <div style={styles.cardLabel}>L/C No</div>
                  <div style={styles.cardValue}>{doc.lc_no || '-'}</div>
                </div>
                <div>
                  <div style={styles.cardLabel}>S.B. No</div>
                  <div style={styles.cardValue}>{doc.sb_no || '-'}</div>
                </div>
              </div>

              {/* Card footer */}
              <div style={styles.cardFooter}>
                <div style={styles.cardAmount}>
                  ${fmt(doc.total_usd) || '0.00'}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  {doc.bill_ref && <span>Ref: {doc.bill_ref}</span>}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Mobile totals */}
        {processedDocs.length > 0 && (
          <div style={styles.mobileTotals}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#047857', textTransform: 'uppercase' }}>
                Total ({processedDocs.length} docs)
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#6b7280' }}>FOB(INR)</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>{fmt(totals.totalFob)}</div>
            </div>
          </div>
        )}
      </>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={styles.page}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={styles.header}>
        <div style={styles.titleWrap}>
          <div style={styles.titleIcon}>
            <FiFileText size={isMobile ? 20 : 24} color="#fff" />
          </div>
          <div>
            <h1 style={styles.title}>Commercial Invoices</h1>
            <p style={styles.subtitle}>
              {activeCompany?.exporter_name || 'All Companies'} &mdash;{' '}
              {processedDocs.length} record{processedDocs.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: isMobile ? 8 : 10, flexWrap: 'wrap' }}>
          <motion.button
            whileHover={isMobile ? {} : { scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handlePrint}
            style={styles.btnSecondary}
          >
            <FiPrinter size={16} />
            {!isMobile && ' Print Report'}
          </motion.button>

          <motion.button
            whileHover={isMobile ? {} : { scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/commercial-invoices/new')}
            style={styles.btnPrimary}
          >
            <FiPlus size={16} />
            {isMobile ? 'New' : 'New Commercial Invoice'}
          </motion.button>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <motion.div
        initial={false}
        animate={{ height: showFilters ? 'auto' : 0, opacity: showFilters ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        style={{ overflow: 'hidden' }}
      >
        <div style={styles.filterCard}>
          <div style={styles.filterRow}>
            {/* Date filters side by side on mobile */}
            {isMobile ? (
              <div style={{ display: 'flex', gap: 10, width: '100%', flexWrap: 'wrap' }}>
                <div style={{ ...styles.filterGroup, flex: 1, minWidth: 120 }}>
                  <label style={styles.filterLabel}>From</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={styles.filterInput}
                  />
                </div>
                <div style={{ ...styles.filterGroup, flex: 1, minWidth: 120 }}>
                  <label style={styles.filterLabel}>To</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    style={styles.filterInput}
                  />
                </div>
              </div>
            ) : (
              <>
                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>From Date</label>
                  <div style={{ position: 'relative' }}>
                    <FiCalendar
                      size={15}
                      style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#059669' }}
                    />
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      style={{ ...styles.filterInput, paddingLeft: 34 }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#10b981';
                        e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(16,185,129,0.2)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                <div style={styles.filterGroup}>
                  <label style={styles.filterLabel}>To Date</label>
                  <div style={{ position: 'relative' }}>
                    <FiCalendar
                      size={15}
                      style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#059669' }}
                    />
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      style={{ ...styles.filterInput, paddingLeft: 34 }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#10b981';
                        e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(16,185,129,0.2)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

              </>
            )}

            <div style={{ ...styles.filterGroup, flex: 1, minWidth: isMobile ? 0 : 200 }}>
              <label style={styles.filterLabel}>Search</label>
              <div style={{ position: 'relative' }}>
                <FiSearch
                  size={15}
                  style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#059669' }}
                />
                <input
                  type="text"
                  placeholder={isMobile ? 'Search...' : 'Search invoice, buyer, commodity, L/C...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ ...styles.filterInput, paddingLeft: 34, width: '100%', boxSizing: 'border-box' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(16,185,129,0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {searchTerm && (
                  <FiX
                    size={15}
                    style={{
                      position: 'absolute',
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#94a3b8',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSearchTerm('')}
                  />
                )}
              </div>
            </div>

            <motion.button
              whileHover={isMobile ? {} : { scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchDocuments}
              style={{
                ...styles.btnSecondary,
                padding: '10px 14px',
                alignSelf: isMobile ? 'stretch' : 'flex-end',
                justifyContent: 'center',
              }}
              title="Refresh"
            >
              <FiRefreshCw size={16} />
              {isMobile && <span style={{ marginLeft: 6 }}>Refresh</span>}
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* ── Toggle Filter Bar ───────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button
          onClick={() => setShowFilters((p) => !p)}
          style={{
            background: 'none',
            border: 'none',
            color: '#059669',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          <FiFilter size={14} />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* ── Mobile Card View ─────────────────────────────────────────────── */}
      {isMobile ? (
        renderMobileCards()
      ) : (
        /* ── Desktop/Tablet Table View ──────────────────────────────────── */
        <motion.div
          layout
          style={styles.tableContainer}
          ref={printRef}
        >
          <div style={styles.tableScroll} className="dl-table-scroll">
            <table style={{ ...styles.table, minWidth: tableMinWidth }}>
              {/* Head */}
              <thead style={styles.thead}>
                <tr>
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{
                        ...styles.th,
                        textAlign: col.align || 'left',
                        cursor: (col.key === '#' || col.key === 'download') ? 'default' : 'pointer',
                        userSelect: 'none',
                        width: col.width || undefined,
                        ...(col.key === 'download' ? {
                          position: 'sticky',
                          right: 0,
                          background: '#047857',
                          boxShadow: '-2px 0 4px rgba(0,0,0,0.1)',
                        } : {}),
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {col.key === 'download' ? <FiEye size={14} /> : col.label}
                        {sortField === col.key && (
                          sortDir === 'asc' ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Body */}
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length}>
                      <div style={styles.loadingOverlay}>
                        <div style={styles.spinner} />
                        <span style={{ color: '#6b7280', fontSize: 14 }}>Loading documents...</span>
                      </div>
                    </td>
                  </tr>
                ) : processedDocs.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length}>
                      <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>
                          <FiFileText size={32} color="#059669" />
                        </div>
                        <h3 style={{ color: '#064e3b', marginBottom: 8, fontFamily: "'Poppins', sans-serif" }}>
                          No Documents Found
                        </h3>
                        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
                          {searchTerm
                            ? 'No documents match your search criteria.'
                            : 'Start by creating your first shipment document.'}
                        </p>
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => navigate('/commercial-invoices/new')}
                          style={styles.btnPrimary}
                        >
                          <FiPlus size={16} /> Create Document
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <AnimatePresence>
                    {processedDocs.map((doc, index) => (
                      <motion.tr
                        key={doc.id || index}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                        style={styles.tr}
                        onClick={() => navigate(`/commercial-invoices/edit/${doc.id}`)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#ecfdf5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        {visibleColumns.map((col) => renderCell(doc, col, index))}
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                )}
              </tbody>

              {/* Footer */}
              {!loading && processedDocs.length > 0 && (
                <tfoot>
                  {(() => {
                    // Totals are keyed by column so they land under the column they
                    // belong to, whichever of them happen to be visible.
                    const totalByKey = {
                      total_usd: totals.totalUsd,
                      fob_inr: totals.totalFob,
                      drawback: totals.totalDrawback,
                    };
                    const firstTotalIdx = visibleColumns.findIndex((c) => c.key in totalByKey);
                    const labelSpan = Math.max(
                      firstTotalIdx === -1 ? visibleColumns.length - 1 : firstTotalIdx,
                      1
                    );

                    return (
                      <tr style={styles.footerRow}>
                        <td
                          colSpan={labelSpan}
                          style={{ ...styles.footerLabel, textAlign: 'right' }}
                        >
                          TOTAL ({processedDocs.length} {processedDocs.length === 1 ? 'Document' : 'Documents'})
                        </td>
                        {visibleColumns.slice(labelSpan).map((col) => (
                          <td key={col.key} style={styles.footerValue}>
                            {col.key in totalByKey ? fmt(totalByKey[col.key]) : ''}
                          </td>
                        ))}
                      </tr>
                    );
                  })()}
                </tfoot>
              )}
            </table>
          </div>
        </motion.div>
      )}

      {/* ── Download Options Modal ──────────────────────────────────────────── */}
      {renderDownloadModal()}

      {/* ── Truck Receipt Edit Modal (shared by the NNC and NCC variants) ───── */}
      {truckReceiptEdit && (
        <TruckReceiptEditModal
          docId={truckReceiptEdit.docId}
          onClose={() => setTruckReceiptEdit(null)}
          onSaved={() => handlePdfPreview(truckReceiptEdit.docId, truckReceiptEdit.docType)}
        />
      )}

      {/* ── PDF Print Data Edit Modal (all other documents) ─────────────────── */}
      {pdfEdit && (
        <PdfDataEditModal
          docId={pdfEdit.docId}
          docType={pdfEdit.docType}
          onClose={() => setPdfEdit(null)}
          onSaved={() => handlePdfPreview(pdfEdit.docId, pdfEdit.docType)}
        />
      )}

      {/* ── Truck Receipt Print Layout Editor (NNC + NCC forms) ─────────────── */}
      {layoutEditor && (
        <TruckReceiptLayoutEditor
          docType={layoutEditor.docType}
          doc={layoutEditor.doc}
          company={layoutEditor.company}
          authority={layoutEditor.authority}
          onClose={() => setLayoutEditor(null)}
          onApply={(layout) => {
            // Regenerate the open preview so it reflects the saved layout
            if (!pdfPreview) return;
            try {
              const gen = pdfPreview.type === 'truckReceiptNCCCalibration'
                ? generateTruckReceiptNCCCalibrationPDF
                : pdfPreview.type === 'truckReceiptNCC'
                  ? generateTruckReceiptNCCPDF
                  : generateTruckReceiptPDF;
              const blob = gen(pdfPreview.doc, pdfPreview.company, pdfPreview.authority, true, layout);
              URL.revokeObjectURL(pdfPreview.url);
              setPdfPreview({ ...pdfPreview, url: URL.createObjectURL(blob) });
            } catch (err) {
              console.error('Failed to refresh preview with saved layout:', err);
            }
          }}
        />
      )}

      {/* ── PDF Preview Modal ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {pdfPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '20px',
            }}
            onClick={closePdfPreview}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              style={{
                background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
                borderRadius: 16,
                maxWidth: '95vw',
                width: 1100,
                maxHeight: '92vh',
                height: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.08)',
                position: 'relative',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Enhanced Header */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                  padding: '20px 28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <FiFileText size={22} color="#ffffff" />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: 20,
                        fontWeight: 700,
                        color: '#ffffff',
                        margin: 0,
                        marginBottom: 4,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {pdfPreview.doc.invoice_no || 'Document'}
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        color: 'rgba(255, 255, 255, 0.85)',
                        fontWeight: 500,
                      }}
                    >
                      {(PDF_MENU_ITEMS.find(i => i.type === pdfPreview.type)?.previewTitle) || 'Document'}
                      {pdfPreview.doc.buyer_name && ` • ${pdfPreview.doc.buyer_name}`}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  {/* Print Layout Button — the truck receipts print onto
                      pre-printed stationery, so their field positions are
                      adjustable and saved as per-company profiles */}
                  {LAYOUT_DOC_TYPES[pdfPreview.type] && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setLayoutEditor({
                        docType: LAYOUT_DOC_TYPES[pdfPreview.type],
                        doc: pdfPreview.doc,
                        company: pdfPreview.company,
                        authority: pdfPreview.authority,
                      })}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 18px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: 10,
                        color: '#ffffff',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      }}
                      title="Adjust where fields print on the pre-printed form"
                    >
                      <FiEdit2 size={16} />
                      {!isMobile && 'Layout'}
                    </motion.button>
                  )}

                  {/* Send Mail Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const pdfTypeLabel = PDF_MENU_ITEMS.find(i => i.type === pdfPreview.type)?.label || 'Document';
                      setEmailModal({
                        to: pdfPreview.doc.buyer_email || '',
                        subject: `${pdfTypeLabel} - ${pdfPreview.doc.invoice_no || 'Document'}`,
                        body: `Dear ${pdfPreview.doc.buyer_name || 'Sir/Madam'},\n\nPlease find attached the ${pdfTypeLabel} for your reference.\n\nRegards,\n${activeCompany?.exporter_name || 'DEY International'}`,
                      });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 10,
                      color: '#ffffff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                    title="Send via Email"
                  >
                    <FiMail size={16} />
                    {!isMobile && 'Mail'}
                  </motion.button>

                  {/* WhatsApp Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      toast.info('WhatsApp integration coming soon');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      background: 'rgba(37, 211, 102, 0.25)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(37, 211, 102, 0.4)',
                      borderRadius: 10,
                      color: '#ffffff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(37, 211, 102, 0.35)';
                      e.currentTarget.style.borderColor = 'rgba(37, 211, 102, 0.6)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(37, 211, 102, 0.25)';
                      e.currentTarget.style.borderColor = 'rgba(37, 211, 102, 0.4)';
                    }}
                    title="Send via WhatsApp"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {!isMobile && 'WhatsApp'}
                  </motion.button>

                  {/* Print Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      window.open(pdfPreview.url, '_blank');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 10,
                      color: '#ffffff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}
                    title="Print PDF"
                  >
                    <FiPrinter size={16} />
                    {!isMobile && 'Print'}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = pdfPreview.url;
                      link.download = `${pdfPreview.type === 'invoice' ? 'Invoice' : 'PackingList'}_${(pdfPreview.doc.invoice_no || 'Document').replace(/\s+/g, '_')}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success('PDF downloaded successfully!');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 18px',
                      background: '#ffffff',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: 10,
                      color: '#047857',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'Inter, sans-serif',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    }}
                    title="Download PDF"
                  >
                    <FiDownload size={16} />
                    {!isMobile && 'Download'}
                  </motion.button>

                  <button
                    onClick={closePdfPreview}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      color: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)';
                      e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.9)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    }}
                    title="Close"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>

              {/* PDF Viewer Area */}
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  background: '#e5e7eb',
                  position: 'relative',
                }}
              >
                <iframe
                  src={`${pdfPreview.url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="PDF Preview"
                />
              </div>

              {/* Footer Info */}
              <div
                style={{
                  padding: '12px 28px',
                  background: '#f9fafb',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                <span style={{ fontWeight: 500 }}>
                  📅 {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <span>
                  💼 {activeCompany?.exporter_name || 'DEY INTL.'}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Email Compose Modal ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {emailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
              padding: 20,
            }}
            onClick={() => !sendingEmail && setEmailModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ duration: 0.25 }}
              style={{
                background: '#ffffff',
                borderRadius: 16,
                width: 480,
                maxWidth: '95vw',
                overflow: 'hidden',
                boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.25)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                  padding: '18px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FiMail size={22} color="#fff" />
                  <h3 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
                    Send Document via Email
                  </h3>
                </div>
                <button
                  onClick={() => !sendingEmail && setEmailModal(null)}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FiX size={18} />
                </button>
              </div>

              {/* Form */}
              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    To (Email Address) *
                  </label>
                  <input
                    type="email"
                    value={emailModal.to}
                    onChange={(e) => setEmailModal({ ...emailModal, to: e.target.value })}
                    placeholder="recipient@example.com"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: '1.5px solid #d1d5db',
                      fontSize: 14,
                      fontFamily: 'Inter, sans-serif',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#10b981')}
                    onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    value={emailModal.subject}
                    onChange={(e) => setEmailModal({ ...emailModal, subject: e.target.value })}
                    placeholder="Email subject"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: '1.5px solid #d1d5db',
                      fontSize: 14,
                      fontFamily: 'Inter, sans-serif',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#10b981')}
                    onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    Message
                  </label>
                  <textarea
                    value={emailModal.body}
                    onChange={(e) => setEmailModal({ ...emailModal, body: e.target.value })}
                    rows={5}
                    placeholder="Email message..."
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: '1.5px solid #d1d5db',
                      fontSize: 14,
                      fontFamily: 'Inter, sans-serif',
                      outline: 'none',
                      resize: 'vertical',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#10b981')}
                    onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                  />
                </div>

                {pdfPreview && (
                  <div style={{
                    padding: '10px 14px',
                    background: '#f0fdf4',
                    borderRadius: 10,
                    border: '1px solid #bbf7d0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 13,
                    color: '#047857',
                  }}>
                    <FiFileText size={16} />
                    <span style={{ fontWeight: 600 }}>
                      {PDF_MENU_ITEMS.find(i => i.type === pdfPreview.type)?.label || 'Document'}_{(pdfPreview.doc.invoice_no || 'Document').replace(/\s+/g, '_')}.pdf
                    </span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
              }}>
                <button
                  onClick={() => !sendingEmail && setEmailModal(null)}
                  disabled={sendingEmail}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 10,
                    border: '1.5px solid #d1d5db',
                    background: '#ffffff',
                    color: '#374151',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: sendingEmail ? 'not-allowed' : 'pointer',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={!sendingEmail ? { scale: 1.03 } : {}}
                  whileTap={!sendingEmail ? { scale: 0.97 } : {}}
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 24px',
                    borderRadius: 10,
                    border: 'none',
                    background: sendingEmail ? '#9ca3af' : 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                    color: '#ffffff',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: sendingEmail ? 'not-allowed' : 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    boxShadow: '0 2px 8px rgba(4, 120, 87, 0.3)',
                  }}
                >
                  <FiSend size={16} />
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
